// src/components/messages/MessageThread.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { messagingService } from '../../services/messagingService';
import { notificationService } from '../../services/notificationService';
import AttachmentCard from './AttachmentCard';
import WelcomeMessageCard from './WelcomeMessageCard';
import MaterialIcon from '../ui/MaterialIcon';

const ACC_STATUSES = [
  { value: 'submitted',                label: 'Submitted',                color: 'bg-slate-100 text-slate-600' },
  { value: 'under_review',             label: 'Under Review',             color: 'bg-blue-100 text-blue-700' },
  { value: 'additional_info_required', label: 'Additional Info Required', color: 'bg-amber-100 text-amber-700' },
  { value: 'approved',                 label: 'Approved',                 color: 'bg-green-100 text-green-700' },
  { value: 'declined',                 label: 'Declined',                 color: 'bg-red-100 text-red-700' },
];

const GENERAL_STATUSES = [
  { value: 'open',   label: 'Open',   color: 'bg-green-100 text-green-700' },
  { value: 'closed', label: 'Closed', color: 'bg-slate-100 text-slate-600' },
];

function StatusPill({ status, type }) {
  const list = type === 'acc' ? ACC_STATUSES : GENERAL_STATUSES;
  const s = list.find(x => x.value === status) || { label: status, color: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.color}`}>
      {s.label}
    </span>
  );
}

function formatTime(ts) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const MessageThread = ({ thread, onBack }) => {
  const { user, impersonating, effectiveRole } = useAuth();
  const [posts, setPosts] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const isAccMember = user?.is_acc_member;
  const isStaff = ['admin', 'board', 'management'].includes(user?.portalRole);
  const canChangeStatus = thread.type === 'acc' && (isAccMember || isStaff);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [p, a] = await Promise.all([
          messagingService.getPosts(thread.id),
          messagingService.getAttachments(thread.id),
        ]);
        if (!cancelled) { setPosts(p); setAttachments(a); }
        if (!impersonating) {
          await messagingService.markRead(thread.id);
          await notificationService.markEntityNotificationsRead({
            userId: user.id,
            entityType: 'message_thread',
            entityId: thread.id,
          });
          window.dispatchEvent(new Event('notifications_read'));
        } else {
          console.warn('[Inbox] Skipping markRead - impersonation session active');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [thread.id, impersonating, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [posts]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    // Reset height so scrollHeight reflects true content height (allows shrink)
    el.style.height = 'auto';
    const sectionHeight = el.closest('section')?.clientHeight;
    const maxHeight = sectionHeight
      ? sectionHeight * (2 / 3)
      : window.innerHeight * 0.48;
    const nextHeight = Math.min(el.scrollHeight, maxHeight);
    el.style.height = nextHeight + 'px';
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [reply]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending) return;

    setSending(true);
    try {
      await messagingService.addPost(thread.id, reply.trim());
      setReply('');
      // Refresh posts
      const p = await messagingService.getPosts(thread.id);
      setPosts(p);
    } catch (err) {
      console.error('Reply failed:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await messagingService.updateStatus(thread.id, newStatus);
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const statusList = thread.type === 'acc' ? ACC_STATUSES : GENERAL_STATUSES;

  return (
    <section className="bg-section-bg rounded-[2.5rem] border border-amber-100/30 flex flex-col h-[calc(100dvh-8rem)] md:h-[72vh] min-h-[560px] overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-start gap-3 p-4 md:p-5 border-b border-amber-100/40 bg-white">
        <button onClick={onBack} className="touch-target rounded-full text-slate-500 hover:text-primary-dark hover:bg-section-bg flex items-center justify-center flex-shrink-0" aria-label="Back to inbox">
          <MaterialIcon name="arrow_back" size={24} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-primary-dark text-base md:text-lg truncate">{thread.subject}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
              thread.type === 'acc' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
            }`}>
              {thread.type === 'acc' ? 'ACC Application' : 'General'}
            </span>
            <StatusPill status={thread.status} type={thread.type} />
          </div>
          {thread.type === 'acc' && thread.acc_details && (
            <p className="text-xs text-slate-500 mt-0.5">
              {[
                thread.acc_details.phase ? `Phase ${thread.acc_details.phase}` : null,
                thread.acc_details.project_type,
                thread.acc_details.address,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>

        {/* Status changer (acc_members and staff only) */}
        {canChangeStatus && (
          <div className="relative flex-shrink-0">
            <select
              value={thread.status}
              onChange={e => handleStatusChange(e.target.value)}
              className="text-base md:text-xs border border-amber-100/40 rounded-full px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-secondary/20 appearance-none pr-8"
            >
              {statusList.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <MaterialIcon name="expand_more" size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Thread attachments (top level) */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 p-4 bg-section-bg border-b border-amber-100/40">
          {attachments.map(a => <AttachmentCard key={a.id} attachment={a} />)}
        </div>
      )}

      {/* Posts */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 p-4 md:p-5">
        {loading ? (
          <div className="flex justify-center pt-8">
            <MaterialIcon name="progress_activity" size={28} className="animate-spin text-secondary/60" />
          </div>
        ) : (
          posts.map((post, index) => {
            const isWelcomeThread = thread?.acc_details?.is_welcome === true;
            
            const targetId = impersonating ? impersonating.id : user?.id;
            
            // Standard messaging logic: If you wrote it, it is outgoing (right side, green)
            const isOutgoing = post.author_id === targetId;
            const targetName = impersonating?.name || impersonating?.full_name || user?.name || '';
            const targetRole = effectiveRole || user?.portalRole || 'member';

            return (
              <div key={post.id} className={`flex flex-col ${isOutgoing ? 'items-end' : 'items-start'} mb-4`}>
                {/* Author Info Outside Bubble */}
                <div className={`text-[11px] font-medium text-slate-500 mb-1.5 ${isOutgoing ? 'pr-1' : 'pl-1'}`}>
                  {post.author?.full_name || post.author?.email || 'Unknown'} · {formatTime(post.created_at)}
                </div>

                {/* Bubble Container */}
                <div className={`max-w-[88%] md:max-w-[75%] px-4 py-3.5 ${
                  isWelcomeThread && index === 0 
                  ? 'bg-transparent max-w-full w-full'
                  : isOutgoing 
                    ? 'bg-primary-dark text-white rounded-[20px] rounded-br-[6px] warm-tactile-shadow'
                    : 'bg-white border text-slate-800 rounded-[20px] rounded-bl-[6px] border-amber-100/60 warm-tactile-shadow'
                }`}>
                  {isWelcomeThread && index === 0 ? (
                    <div className={!isOutgoing ? 'border border-amber-100/60 rounded-[18px] overflow-hidden bg-white warm-tactile-shadow' : ''}>
                      <WelcomeMessageCard
                        recipientName={targetName}
                        recipientRole={targetRole}
                        personalNote={post.body}
                        senderName={thread.acc_details?.sender_name || post.author?.full_name}
                        senderTitle={thread.acc_details?.sender_title}
                        hoaName={thread.acc_details?.hoa_name}
                      />
                    </div>
                  ) : (
                    <p className="text-[14px] leading-relaxed whitespace-pre-wrap break-words">{post.body}</p>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      {thread.status !== 'approved' && thread.status !== 'declined' && thread.status !== 'closed' && (
        <form onSubmit={handleReply} className="mobile-sticky-actions flex gap-2 p-3 md:p-4 border-t border-amber-100/40 bg-white">
          <textarea
            ref={textareaRef}
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Write a reply…"
            rows={2}
            className="flex-1 min-w-0 px-4 py-3 text-base md:text-sm rounded-2xl border border-amber-100/60 focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(e); } }}
          />
          <button
            type="submit"
            disabled={!reply.trim() || sending}
            className="touch-target flex items-center justify-center self-end rounded-full bg-secondary text-white disabled:opacity-50 transition-colors hover:brightness-110 active:scale-95"
            aria-label="Send reply"
          >
            <MaterialIcon name={sending ? 'progress_activity' : 'send'} size={20} className={sending ? 'animate-spin' : ''} />
          </button>
        </form>
      )}
    </section>
  );
};

export default MessageThread;
